import Link from "@/components/ui/RouterLink";
import { Mail, Phone, MapPin } from "lucide-react";
import { Logo } from "./Logo";
import { footerNav } from "@/config/navigation";
import { contact } from "@/config/contact";
import { whmcs } from "@/config/whmcs";
import { brand } from "@/config/brand";

function resolveHref(href: string) {
  if (href === "whmcs:login") return whmcs.loginUrl;
  if (href === "whmcs:store") return whmcs.storeUrl;
  if (href === "whmcs:support") return whmcs.supportUrl;
  return href;
}

function FooterLink({ href, label }: { href: string; label: string }) {
  const resolved = resolveHref(href);
  const isExternal = resolved.startsWith("http");
  if (isExternal) {
    return (
      <a href={resolved} target="_blank" rel="noopener noreferrer" className="focus-ring text-sm text-text-secondary transition hover:text-cyan">
        {label}
      </a>
    );
  }
  return (
    <Link href={resolved} className="focus-ring text-sm text-text-secondary transition hover:text-cyan">
      {label}
    </Link>
  );
}

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-navy-secondary">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-5">
          <div className="col-span-2">
            <Logo />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-text-secondary">
              {brand.descriptor} for modern digital businesses.
            </p>
            <div className="mt-5 space-y-2">
              <a href={`mailto:${contact.email}`} className="focus-ring flex items-center gap-2 text-sm text-text-secondary hover:text-cyan">
                <Mail className="h-3.5 w-3.5" /> {contact.email}
              </a>
              <a href={contact.phoneHref} className="focus-ring flex items-center gap-2 text-sm text-text-secondary hover:text-cyan">
                <Phone className="h-3.5 w-3.5" /> {contact.phone}
              </a>
              <p className="flex items-start gap-2 text-sm text-text-secondary">
                <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {contact.address.line2}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.12em] text-text-muted">Products</p>
            <ul className="space-y-3">
              {footerNav.products.map((item) => (
                <li key={item.href}><FooterLink {...item} /></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.12em] text-text-muted">Company</p>
            <ul className="space-y-3">
              {footerNav.company.map((item) => (
                <li key={item.href}><FooterLink {...item} /></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.12em] text-text-muted">Legal</p>
            <ul className="space-y-3">
              {footerNav.legal.map((item) => (
                <li key={item.href}><FooterLink {...item} /></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 sm:flex-row">
          <p className="text-xs text-text-muted">© {year} The Planet. All rights reserved.</p>
          <div className="flex gap-5 text-xs text-text-muted">
            <FooterLink href="/legal/privacy-policy" label="Privacy" />
            <FooterLink href="/legal/terms-and-conditions" label="Terms" />
            <FooterLink href="/legal/privacy-policy" label="Cookies" />
          </div>
        </div>
      </div>
    </footer>
  );
}
